import type { TranslationKey } from '@/i18n/translations'
import { describeScanEvent } from './fh-vitals-events'
import { pickBestResult } from './fh-vitals-result'
import type { FhVitalsClient, FhVitalsEvent, FhVitalsResult } from './fh-vitals-types'

export interface ScanDemographics {
  readonly heightCm: number
  readonly weightKg: number
  readonly age: number
  readonly sex: 0 | 1
}

export interface ScanSessionConfig {
  /** Seconds the SDK asks the patient to hold still before measuring starts. */
  readonly cameraPrepareSeconds: number
  /** The measurement window; faceheart2.html uses 55s inside the SDK's ~50s envelope. */
  readonly measureWindowMs: number
  readonly assetsFolderPath: string
  readonly authUrl: string
  readonly virtualId?: string | null
  readonly canvasId: string
}

export type ScanSessionOutcome =
  | { readonly outcome: 'measured'; readonly result: FhVitalsResult }
  | { readonly outcome: 'no-usable-result' }
  | { readonly outcome: 'error'; readonly messageKey: TranslationKey }

/**
 * Runs one scan end to end: init -> preview -> camera-prepare countdown -> startMeasuring (only
 * after the countdown - v5.9.3's startMeasuring() internally waits for face detection, so starting
 * it immediately races the countdown the patient is still looking at) -> the measurement window ->
 * stop. Takes an FhVitalsClient rather than reaching for `window.FHVitalsSDK` itself, which is what
 * lets every branch below be driven by a fake client under Vitest's fake timers instead of a real
 * camera and a real 55 seconds.
 */
export async function runScanSession(
  client: FhVitalsClient,
  demographics: ScanDemographics,
  config: ScanSessionConfig,
  onEvent?: (event: FhVitalsEvent) => void,
): Promise<ScanSessionOutcome> {
  let bestResult: FhVitalsResult | null = null
  let endedEarlyWith: TranslationKey | null = null
  let resolveEndedEarly!: () => void
  const endedEarly = new Promise<void>((resolve) => {
    resolveEndedEarly = resolve
  })

  const initResult = await client.init({
    on_result: (result) => {
      bestResult = pickBestResult(bestResult, result)
    },
    on_event: (event) => {
      onEvent?.(event)
      const description = describeScanEvent(event)
      if (description.endsSession && endedEarlyWith === null) {
        endedEarlyWith = description.messageKey
        resolveEndedEarly()
      }
    },
    config: {
      camera_prepare_second: config.cameraPrepareSeconds,
      assets_folder_path: config.assetsFolderPath,
      auth_url: config.authUrl,
    },
  })

  // ERROR_HAS_INITIALIZED is success on a warm reload, not a failure (task brief; faceheart2.html).
  if (initResult.error !== 'ERROR_NONE' && initResult.error !== 'ERROR_HAS_INITIALIZED') {
    return { outcome: 'error', messageKey: 'scanCouldNotStart' }
  }

  const previewResult = await client.startPreview(config.canvasId, 'user')
  if (previewResult.error !== 'ERROR_NONE') {
    return { outcome: 'error', messageKey: 'scanCouldNotStart' }
  }

  const afterPrepare = await raceElapsedAgainst(config.cameraPrepareSeconds * 1000, endedEarly)
  if (afterPrepare === 'ended-early') {
    await client.stopPreview()
    return { outcome: 'error', messageKey: endedEarlyWith! }
  }

  // Kicked off only after the countdown: v5.9.3's startMeasuring() promise resolves only once a
  // face is found (or FACE_NOT_DETECTED comes back), so starting it immediately would have it
  // racing the countdown the patient is still looking at (task brief; faceheart2.html).
  const measureResult = await client.startMeasuring({
    height: demographics.heightCm,
    weight: demographics.weightKg,
    age: demographics.age,
    sex: demographics.sex,
    bp_mode: 'binary',
    bp_group: 'normal',
    virtual_id: config.virtualId ?? undefined,
  })
  if (measureResult.error !== 'ERROR_NONE') {
    await client.stopPreview()
    return { outcome: 'error', messageKey: 'scanCouldNotStart' }
  }

  const afterWindow = await raceElapsedAgainst(config.measureWindowMs, endedEarly)

  await client.stopMeasuring()
  await client.stopPreview()

  if (afterWindow === 'ended-early' && !bestResult) {
    return { outcome: 'error', messageKey: endedEarlyWith! }
  }

  // Results arrive incrementally for many seconds before a full set (task brief); the timeout picks
  // whatever the latest usable frame was rather than waiting for a perfect one that may never come.
  return bestResult ? { outcome: 'measured', result: bestResult } : { outcome: 'no-usable-result' }
}

async function raceElapsedAgainst(
  ms: number,
  ended: Promise<void>,
): Promise<'elapsed' | 'ended-early'> {
  return Promise.race([
    new Promise<'elapsed'>((resolve) => setTimeout(() => resolve('elapsed'), ms)),
    ended.then((): 'ended-early' => 'ended-early'),
  ])
}
